import React, { useState, useRef, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import jsonData from "./Male_cene.json";
import { useTranslation } from 'react-i18next';  // Import translation hook
import "./App.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AnalgesicPricesPage = () => {
  const { t } = useTranslation();  // Get translation function
  const [data, setData] = useState([]);
  const [isLogScaleBTC, setIsLogScaleBTC] = useState(true);
  const [isLogScaleEUR, setIsLogScaleEUR] = useState(false);
  const [showEUR, setShowEUR] = useState(false);
  const [showSATS, setShowSATS] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    const analgesicPrices = jsonData["0411002S"]["Analgetik (Aspirin), 20 tablet (škatla)"];

    const formattedData = Object.keys(analgesicPrices).map((year) => ({
      Year: year,
      PriceEUR: analgesicPrices[year]?.EUR || null,
      PriceBTC: analgesicPrices[year]?.BTC || null,
    }));

    setData(formattedData);
  }, []);

  const calculatePercentageChange = (startYear, endYear, key) => {
    const startData = data.find((row) => row.Year === startYear);
    const endData = data.find((row) => row.Year === endYear);
    if (startData && endData && startData[key] !== null && endData[key] !== null) {
      return (((endData[key] - startData[key]) / startData[key]) * 100).toFixed(2);
    }
    return "N/A";
  };

  const periods = [
    { label: t('global_year_1'), duration: 1 },
    { label: t('global_year_3'), duration: 3 },
    { label: t('global_year_5'), duration: 5 },
    { label: t('global_year_10'), duration: 10 },
  ];

  const currentYear = Math.max(...data.map((row) => parseInt(row.Year)));
  const rows = periods.map(({ label, duration }) => {
    const startYear = (currentYear - duration).toString();
    return {
      label,
      btcChange: calculatePercentageChange(startYear, currentYear.toString(), "PriceBTC"),
      eurChange: calculatePercentageChange(startYear, currentYear.toString(), "PriceEUR"),
    };
  });

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  if (data.length === 0) {
    return <p className="loading">t('global_loading_data')</p>;
  }

  const filteredData = data.filter((row) => showEUR || row.PriceBTC !== null);

  const years = filteredData.map((row) => row.Year);
  const pricesEUR = filteredData.map((row) => row.PriceEUR);
  const pricesBTC = filteredData.map((row) => (showSATS ? row.PriceBTC * 1e8 : row.PriceBTC));

  const formatNumber = (num) => {
    return num.toLocaleString("sl-SI", { maximumFractionDigits: 6 });
  };

  const chartData = {
    labels: years,
    datasets: [
      {
        label: `${t('aspirin_tooltip')} - (${showSATS ? "SATS" : "฿"})`,
        data: pricesBTC,
        borderColor: "orange",
        yAxisID: "btc",
        fill: false,
      },
      ...(showEUR
        ? [
            {
              label: `${t('aspirin_tooltip')} - (€)`,
              data: pricesEUR,
              borderColor: "#4CAF50",
              yAxisID: "eur",
              fill: false,
            },
          ]
        : []),
    ],
  };

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: "#ffffff",
      },
      onClick: null,
    },
    tooltip: {
      mode: "index",
      intersect: false,
      enabled: false,
      external: (context) => {
        const chart = context.chart;
        const tooltipModel = context.tooltip;

        // Remove any existing tooltips
        let tooltipContainers = document.getElementsByClassName("chartjs-custom-tooltip");
        while (tooltipContainers.length > 0) {
          tooltipContainers[0].parentNode.removeChild(tooltipContainers[0]);
        }

        // Check if active points exist
        if (!chart.tooltip._active || !chart.tooltip._active.length) {
          const timeTooltipEl = document.getElementById("chartjs-time-tooltip");
          if (timeTooltipEl) timeTooltipEl.style.opacity = "0";
          return;
        }

        const tooltipPositions = [];

        // Handle dataset tooltips
        chart.tooltip._active.forEach((activePoint) => {
          const datasetIndex = activePoint.datasetIndex;
          const index = activePoint.index;
          const datasetLabel = chart.data.datasets[datasetIndex].label;
          const value = chart.data.datasets[datasetIndex].data[index];

          const tooltipEl = document.createElement("div");
          tooltipEl.className = "chartjs-custom-tooltip";
          tooltipEl.innerHTML = `
            <div>${datasetLabel}: ${value.toLocaleString("sl-SI", {
              maximumFractionDigits: 5,
            })}</div>
          `;

          const position = chart.canvas.getBoundingClientRect();
          const xPos = position.left + window.pageXOffset + activePoint.element.x;
          const yPos = position.top + window.pageYOffset + activePoint.element.y - 20;

          tooltipPositions.push({ tooltipEl, xPos, yPos, height: 30 }); // Assuming 30px tooltip height

          // Append the tooltip to the body
          document.body.appendChild(tooltipEl);
        });

        // Adjust positions to prevent overlap
        tooltipPositions.forEach((tooltip, index) => {
          for (let i = 0; i < index; i++) {
            const prevTooltip = tooltipPositions[i];
            const overlap = Math.abs(prevTooltip.yPos - tooltip.yPos) < prevTooltip.height;

            if (overlap) {
              // Adjust the current tooltip's Y position to avoid overlap
              tooltip.yPos = prevTooltip.yPos + prevTooltip.height + 5; // Add some extra spacing
            }
          }

          tooltip.tooltipEl.style.left = `${tooltip.xPos}px`;
          tooltip.tooltipEl.style.top = `${tooltip.yPos}px`;
        });

        // Handle the bottom time tooltip
        let timeTooltipEl = document.getElementById("chartjs-time-tooltip");
        if (!timeTooltipEl) {
          timeTooltipEl = document.createElement("div");
          timeTooltipEl.id = "chartjs-time-tooltip";
          document.body.appendChild(timeTooltipEl);
        }

        // Get the active X-axis label (time reference)
        const index = chart.tooltip._active[0].index;
        const time = chart.data.labels[index];

        // Update tooltip content
        timeTooltipEl.innerHTML = `<div>${time}</div>`;
        timeTooltipEl.style.opacity = "1";

        // Calculate position
        const activePoint = chart.tooltip._active[0].element;
        const canvasPosition = chart.canvas.getBoundingClientRect();

        // Set tooltip position dynamically
        const tooltipWidth = timeTooltipEl.offsetWidth; // Ensure width is calculated after rendering
        timeTooltipEl.style.left = `${
          canvasPosition.left + window.pageXOffset + activePoint.x - tooltipWidth / 2
        }px`;
        timeTooltipEl.style.top = `${canvasPosition.bottom + window.pageYOffset + 10}px`;
      },
    },
  },
    scales: {
      btc: {
        type: isLogScaleBTC ? "logarithmic" : "linear",
        position: "left",
        title: {
          display: true,
          text: `(${showSATS ? "SATS" : "฿"} - ${isLogScaleBTC ? t('global_logarithmic') : t('global_linear')})`,
        },
        ticks: {
          callback: (value) => formatNumber(value),
        },
      },
      ...(showEUR
        ? {
            eur: {
              type: isLogScaleEUR ? "logarithmic" : "linear",
              position: "right",
              title: {
                display: true,
                text: isLogScaleEUR ? `(€ - ${t('global_logarithmic')})` : "(€)",
              },
              ticks: {
                callback: (value) => formatNumber(value),
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          }
        : {}),
    },
  };

// Vertical Line Plugin
const verticalLinePlugin = {
  id: "verticalLinePlugin",
  beforeDraw: (chart) => {
    if (chart.tooltip._active && chart.tooltip._active.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0].element;
      const x = activePoint.x;
      const chartArea = chart.chartArea;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.8)";
      ctx.stroke();
      ctx.restore();
    }
  },
};

// Register the Plugin
ChartJS.register(verticalLinePlugin);

  return (
    <div className="chart-container">
      <h2 className="chart-title">{t('aspirin_title')}</h2>
      <Line ref={chartRef} data={chartData} options={chartOptions} />
      <table className="price-change-table">
        <thead>
          <tr>
            <th></th>
            {rows.map((row) => (
              <th key={row.label}>{row.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>BTC</td>
            {rows.map((row) => (
              <td key={row.label}>{row.btcChange} %</td>
            ))}
          </tr>
          <tr>
            <td>EUR</td>
            {rows.map((row) => (
              <td key={row.label}>{row.eurChange} %</td>
            ))}
          </tr>
        </tbody>
      </table>
      <div className="button-container">
        <button
          className="toggle-button"
          onClick={() => setShowEUR(!showEUR)}
        >
          {showEUR ? t('global_hide_eur_prices') : t('global_show_eur_prices')}
        </button>

        <button
          className={`toggle-button ${showSATS ? "sats-active" : "btc-active"}`}
          onClick={() => setShowSATS(!showSATS)}
        >
          {showSATS ? t('global_show_in_btc') : t('global_show_in_sats')}
        </button>
      </div>

      <div className="checkbox-container">
        <label>
          <input
            type="checkbox"
            checked={isLogScaleBTC}
            onChange={(e) => setIsLogScaleBTC(e.target.checked)}
          />
          {t('global_logarithmic_scale_btc')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={isLogScaleEUR}
            onChange={(e) => setIsLogScaleEUR(e.target.checked)}
            disabled={!showEUR}
          />
          {t('global_logarithmic_scale_eur')}
        </label>
      </div>
	  <p className="vir">{t('global_source_SURS_Kraken')}</p>
	  <hr></hr>
	  <h3 className="razlaga">{t('kaj_predstavljalate_graf_tabela_title')}</h3>
	  <p className="razlaga">{t('kaj_predstavljalate_graf_tabela')}</p>
	  <h3 className="razlaga">{t('od_kje_pridejo_cene_v_evrih_title')}</h3>
	  <p className="razlaga">{t('od_kje_pridejo_cene_v_evrih')}</p>
	  <h3 className="razlaga">{t('kako_se_izračuna_bitcoin_cena_title')}</h3>
	  <p className="razlaga">{t('kako_se_izračuna_bitcoin_cena')}</p>
    </div>
  );
};

export default AnalgesicPricesPage;
